/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { BringOnline } from './bring-online';
import { appConstants } from '@app/common/app-constants';
import { StudyDataParam } from '@app/common/services/bring-online/bring-online.types';
import { UtilService } from '@app/util/util.service';

export class PacsBringOnline extends BringOnline {
  protected constructor() {
    super();
  }

  getBringOnlineStatus(id: any, studyDataParam: StudyDataParam): string {
    let fetchStatus: string;
    if (
      this.inProgress.indexOf(id) >= 0 ||
      studyDataParam.archiveAdoptedStatus === appConstants.archiveAdoptionStatus.InProgress
    ) {
      fetchStatus = appConstants.archiveAdoptionStatus.InProgress;
    } else if (this.error.indexOf(id) >= 0) {
      fetchStatus = appConstants.archiveAdoptionStatus.Error;
    } else if (studyDataParam.studyDataAvailability === appConstants.studyDataAvailability.THIRD_PARTY) {
      fetchStatus = appConstants.studyDataAvailability.THIRD_PARTY;
    } else if (
      studyDataParam.archiveAdoptedStatus === appConstants.archiveAdoptionStatus.NO &&
      studyDataParam.studyDataAvailability === appConstants.studyDataAvailability.ONLINE
    ) {
      fetchStatus = appConstants.archiveAdoptionStatus.NO;
    } else if (studyDataParam.archiveAdoptedStatus === appConstants.archiveAdoptionStatus.ArchiveAdopted) {
      fetchStatus = appConstants.archiveAdoptionStatus.ArchiveAdopted;
    } else if (this.isOfflineExam(studyDataParam)) {
      fetchStatus = appConstants.studyDataAvailability.OFFLINE;
    } else {
      fetchStatus = appConstants.archiveAdoptionStatus.NO;
    }
    return fetchStatus;
  }

  getPopupParams(workItem: any) {
    const studyDataParam: StudyDataParam = UtilService.fetchStudyDataParam(workItem);
    // Show dialog only for Archived work item and yet to initiate download
    const bringOnlineStatus = this.getBringOnlineStatus(workItem, studyDataParam);
    if (
      bringOnlineStatus === appConstants.archiveAdoptionStatus.ArchiveAdopted ||
      bringOnlineStatus === appConstants.studyDataAvailability.THIRD_PARTY
    ) {
      if (UtilService.disableBringOnline(bringOnlineStatus, workItem)) {
        return {
          isDownloadable: true,
          isZeroImageExam: true
        };
      } else {
        // Popup for downloadable exam
        return {
          isDownloadable: true,
          isZeroImageExam: false
        };
      }
    } else if (
      !UtilService.isRemoteExam(workItem) &&
      bringOnlineStatus === appConstants.studyDataAvailability.OFFLINE
    ) {
      // Popup for non downloadable exam, remote exams should not consider studyDataAvailability field
      return {
        isDownloadable: false,
        isZeroImageExam: false
      };
    }
  }

  isOfflineExam(studyDataParam: any): boolean {
    return (
      !_.isNull(studyDataParam.studyDataAvailability) &&
      !_.isUndefined(studyDataParam.studyDataAvailability) &&
      ((studyDataParam.studyDataAvailability === appConstants.studyDataAvailability.UNAVAILABLE &&
        studyDataParam.imageCount > 0) ||
        studyDataParam.studyDataAvailability === appConstants.studyDataAvailability.OFFLINE ||
        studyDataParam.studyDataAvailability === appConstants.studyDataAvailability.CENTERA)
    );
  }

  isExamAvailable(workItem: any): boolean {
    const studyDataAvailability =
      workItem.studyDataAvailability !== undefined ? workItem.studyDataAvailability : workItem.availability;
    const isArchived =
      workItem.archiveAdoptedStatus !== undefined
        ? workItem.archiveAdoptedStatus !== 'NO'
        : workItem.archiveAdopted === true;
    const studyDataParam = {
      studyDataAvailability: studyDataAvailability,
      imageCount: workItem.imageCount,
      archiveAdoptedStatus: isArchived
    };
    if (UtilService.isRemoteExam(workItem) || UtilService.isArchivedExam(isArchived)) {
      return true;
    }
    if (this.inProgress.indexOf(workItem.id) >= 0 || this.error.indexOf(workItem.id) >= 0) {
      return false;
    }
    // exam is local
    return !this.isOfflineExam(studyDataParam) && !UtilService.isThirdParty(studyDataAvailability);
  }
}
